import React, { Fragment, useMemo, } from 'react';
import { isEmpty, orderBy, sortBy, keyBy, } from 'lodash';
import { toast } from 'react-toastify';
import { format as formatDate, addDays, addMonths, startOfDay, endOfDay, } from 'date-fns';
import numeral from 'numeral';

import firebase from '../../firebase';
import { colors, } from '../../shared/config';
import { canUpdateContainer } from '../../shared/abilities';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import ContainerFormModal from '../modals/ContainerFormModal';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useQueryParams from '../hooks/useQueryParams';
import AddInTenantButton from '../AddInTenantButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import QuerySortLabel from '../QuerySortLabel';
import InventoriesHeader from '../InventoriesHeader';
import { fields, } from '../../shared/models/container';
import QuerySelector from '../QuerySelector';
import QueryDateRangeSelector from '../QueryDateRangeSelector';

const { keys, entries } = Object;
const db = firebase.firestore();

export default AdminPage(function AdminInventoriesSchedule (props) {
  const { tenant, user } = props;
  const queryParams = useQueryParams();
  const departureStartOn = useMemo(_ => queryParams.departureDateRange?.[0] ? startOfDay(new Date(queryParams.departureDateRange[0])) : null, [queryParams.departureDateRange]);
  const departureEndOn = useMemo(_ => queryParams.departureDateRange?.[1] ? endOfDay(new Date(queryParams.departureDateRange[1])) : null, [queryParams.departureDateRange]);
  const arrivalStartOn = useMemo(_ => queryParams.arrivalDateRange?.[0] ? startOfDay(new Date(queryParams.arrivalDateRange[0])) : null, [queryParams.arrivalDateRange]);
  const arrivalEndOn = useMemo(_ => queryParams.arrivalDateRange?.[1] ? endOfDay(new Date(queryParams.arrivalDateRange[1])) : null, [queryParams.arrivalDateRange]);
  const productTypes = useCollectionSubscriptionInTenant(db.collection('productTypes').orderBy('index'));
  const productTypesById = keyBy(productTypes, 'id');
  const productTypeOptions = productTypes.map(_ => ({ value: _.id, label: _.name }));
  const products = useCollectionSubscriptionInTenant(db.collection('products').orderBy('code'));
  const bodyProducts = products.filter(_ => _.isBody);
  const productOptions = bodyProducts.map(_ => ({ value: _.id, label: _.code }));
  const productsById = keyBy(products, 'id');
  const suppliers = sortBy(useCollectionSubscriptionInTenant(db.collection('suppliers')), 'name');
  const suppliersById = keyBy(suppliers, 'id');
  const purchaseOrders = orderBy(useCollectionSubscriptionInTenant(db.collection('purchaseOrders')), _ => _.orderedOn.toDate());
  const purchaseOrdersById = keyBy(purchaseOrders, 'id');
  const _containers = useCollectionSubscriptionInTenant(db.collection('containers'));
  const containers = useMemo(_ => orderBy(_containers, _ => _.number, queryParams.sort?.direction ?? 'asc'), [_containers, queryParams.sort?.direction]);

  let filteredRowGroups = containers;
  if(departureStartOn != null) {
    filteredRowGroups = filteredRowGroups.filter(_ => _.departureDate.toDate() >= departureStartOn);
  }
  if(departureEndOn != null) {
    filteredRowGroups = filteredRowGroups.filter(_ => _.departureDate.toDate() <= departureEndOn);
  }
  if(arrivalStartOn != null) {
    filteredRowGroups = filteredRowGroups.filter(_ => _.arrivalDate.toDate() >= arrivalStartOn);
  }
  if(arrivalEndOn != null) {
    filteredRowGroups = filteredRowGroups.filter(_ => _.arrivalDate.toDate() <= arrivalEndOn);
  }
  filteredRowGroups = filteredRowGroups.map((rowGroup) => {
    let filteredItems = rowGroup.items;
    if(!isEmpty(queryParams.productTypeIds)) {
      filteredItems = filteredItems.filter(_ => productsById[_.productId]?.productTypeIds?.some(_ => queryParams.productTypeIds.includes(_)));
    }
    if(!isEmpty(queryParams.productIds)) {
      filteredItems = filteredItems.filter(_ => queryParams.productIds.includes(_.productId));
    }
    return {
      ...rowGroup,
      filteredItems,
    };
  }).filter(_ => _.filteredItems.length > 0);

  return (
    <div>
      <div className="admin-containers container py-5 position-relative bg-white">
        <InventoriesHeader activeTab="containers" />
        <div className='mt-2 d-flex align-items-end flex-wrap gap-2'>
          <QueryDateRangeSelector label="工場出荷予定日" defaultValue={[departureStartOn, departureEndOn]} paramName="departureDateRange" pickerProps={{ isClearable: true, showYearDropdown: true, dropdownMode: 'select' }} />
          <QueryDateRangeSelector label="倉庫到着予定日" defaultValue={[arrivalStartOn, arrivalEndOn]} paramName="arrivalDateRange" pickerProps={{ isClearable: true, showYearDropdown: true, dropdownMode: 'select' }} />
          <QuerySelector paramName="productTypeIds" width={400} isMulti options={productTypeOptions} label="商品種別で絞込み" />
          <QuerySelector paramName="productIds" width={300} isMulti options={productOptions} label="商品で絞込み" />
        </div>
        <div className="mt-4 d-flex justify-content-end mb-3">
          <AddInTenantButton itemRef={db.collection('containers').doc()} FormModal={ContainerFormModal} formProps={{ purchaseOrders, products: bodyProducts, otherContainers: containers, suppliers, }} disabled={!canUpdateContainer(user)} />
        </div>
        <div>
          {
            containers.length > 0 ? (
              <table className="table table-bordered table-sm">
                <thead className="thead-light text-center">
                  <tr>
                    <th style={{ width: 200 }}></th>
                    <th>
                      <QuerySortLabel field="number" defaultActive defaultDirection="asc">
                        コンテナ No.
                      </QuerySortLabel>
                    </th>
                    <th>仕入先</th>
                    <th>工場出荷予定日</th>
                    <th>倉庫到着予定日</th>
                    <th style={{ minWidth: 150, }}>メモ</th>
                    <th>PO No.</th>
                    <th>商品コード</th>
                    <th>数量</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    filteredRowGroups.map((container) => {
                      const { id, ref, number, supplierId, departureDate, arrivalDate, filteredItems, note, } = container;

                      return (
                        <Fragment key={id}>
                          {
                            filteredItems.map((item, i) => {
                              const { purchaseOrderId, productId, quantity, } = item;
                              const purchaseOrder = purchaseOrdersById[purchaseOrderId];
                              const product = productsById[productId];
                              return (
                                <tr key={id}>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={filteredItems.length} className="text-nowrap">
                                          <EditButton itemRef={ref} FormModal={ContainerFormModal} formProps={{ purchaseOrders, products: bodyProducts, otherContainers: containers.filter(_ => _.id !== id), suppliers, }} disabled={!canUpdateContainer(user)} />
                                          <DeleteButton itemRef={ref} className="ml-2" disabled={!canUpdateContainer(user)} />
                                        </td>
                                        <td rowSpan={filteredItems.length}>
                                          {number}
                                        </td>
                                        <td rowSpan={filteredItems.length}>
                                          {suppliersById[supplierId]?.name}
                                        </td>
                                        <td rowSpan={filteredItems.length}>
                                          {formatDate(departureDate.toDate(), 'yyyy/MM/dd')}
                                        </td>
                                        <td rowSpan={filteredItems.length}>
                                          {formatDate(arrivalDate.toDate(), 'yyyy/MM/dd')}
                                        </td>
                                        <td rowSpan={filteredItems.length} style={{ whiteSpace: 'pre-line', }}>
                                          {note}
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                  <td>
                                    {purchaseOrder?.poNumber}
                                  </td>
                                  <td>
                                    {product?.code}
                                  </td>
                                  <td className="text-right">
                                    {numeral(quantity).format()}
                                  </td>
                                </tr>
                              );
                            })
                          }
                        </Fragment>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                コンテナは未登録です
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
